import React, { createRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TranslateBadge } from '@travel/icons/ui';
import { seoKeys } from '@travel/traveler-core/utils/seo';
import { isNotEmptyArray, stringify } from '@travel/utils';

import WithSkeletonLoading from 'components/WithSkeletonLoading';

import { replaceLocation } from 'store/__router/actions';
import { getIsFetching, getItems } from 'store/providerRatePlan/selectors';

import { Translate } from 'core/translate';
import paths from 'core/universalRouter/paths';
import { ProviderInfoQuery } from 'ProviderInformation-Types';

import NotFoundBox from '../NotFoundBox';
import RoomPlansItem from './RoomPlansItem';

import styles from './roomList.module.scss';

type Props = {
  className?: string;
  /** ClassName when the list is empty */
  emptyClassName?: string;
  /** String to specify the page identifier */
  pageIdentifier: string;
  /** Provider Information Canonical URL */
  providerInfoCanonicalUrl: string;
  /** Current query object */
  query: ProviderInfoQuery;
  /** on click function more dates prompt */
  moreDateClick?: () => void;
};

type PriceRangeType = number | null | undefined;

export type PlanFilter = {
  /** Array of string to define current selected features */
  features?: Array<string>;
  /** Number of filtered min price */
  minPrice?: PriceRangeType;
  /** Number of filtered max price */
  maxPrice?: PriceRangeType;
  /** boolean of current selected free cancellation*/
  freeCancellation?: boolean;
};

function RoomList(props: Props) {
  const { moreDateClick, pageIdentifier, query } = props;

  const dispatch = useDispatch();

  const items = useSelector(getItems);
  const itemRefs = useMemo(() => items?.map(() => createRef<HTMLDivElement>()), [items]);
  const isFetching = useSelector(getIsFetching);

  const updateURL = useCallback(
    search => {
      // to update URL on query changed
      dispatch(
        replaceLocation({
          pathname: paths.providerInfo.pathResolver(props.providerInfoCanonicalUrl),
          search,
        }),
      );
    },
    [dispatch, props.providerInfoCanonicalUrl],
  );

  const onSelectDate = useCallback(
    (startDate?: string, endDate?: string) => {
      if (!(startDate && endDate)) {
        return null;
      }
      updateURL(
        stringify({ ...query, [seoKeys.startDate]: startDate, [seoKeys.endDate]: endDate }),
      );
    },
    [query, updateURL],
  );

  const onMoreDateClick = useCallback(() => {
    moreDateClick?.();
  }, [moreDateClick]);

  const isGoogleTranslated = useMemo(() => {
    return items.some(roomItem => roomItem.isGoogleTranslated);
  }, [items]);

  return (
    <div className={isNotEmptyArray(items) ? props.className : props.emptyClassName}>
      <WithSkeletonLoading
        count={isNotEmptyArray(items) ? items.length : 1}
        row={5}
        length="long"
        isLoading={isFetching}
        customStyle="textWithImageRow"
        skeletonItemClassName={styles.loader}
        skeletonImageClassName={styles.loaderImage}
      >
        {isNotEmptyArray(items) ? (
          <div data-testid="ratePlanList-wrapper" className={styles.wrapper}>
            <Translate
              id="Provider_Info.Pagination.Room.Totals"
              data={{
                number_of_item_types: items.length,
              }}
              className={styles.totalRooms}
              count={items.length}
            />
            {items.map((item, i) => (
              <div key={item.id} ref={itemRefs[i]}>
                <RoomPlansItem
                  providerId={pageIdentifier}
                  itemId={item.id}
                  searchParam={query}
                  onSelectDate={onSelectDate}
                  isFirst={i === 0}
                />
              </div>
            ))}
            {isGoogleTranslated && (
              <div className={styles.translateBadge}>
                <TranslateBadge data-testid="ratePlanList-translateBadge" />
              </div>
            )}
          </div>
        ) : (
          <NotFoundBox moreDateClick={onMoreDateClick} />
        )}
      </WithSkeletonLoading>
    </div>
  );
}

export default React.memo(RoomList);
